import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ExternalLink, Loader2, Maximize2, MessageSquarePlus, Minimize2, X } from 'lucide-react';
import { useZoeConversation } from '../../contexts/ZoeConversationContext';
import { Button } from '@/components/ui/button';
import QueryBar, { PROFIT_HIGHLIGHTED_CHIP_WORDS_RE } from '../investigator/QueryBar';
import ZoeMark from './ZoeMark';
import ZoeThread from './ZoeThread';
import { ZOE_MORE_PROMPTS, ZOE_PRIMARY_PROMPTS } from './zoePrompts';

const ZOE_POPUP_ID = 'zoe-popup';
const ZOE_POPUP_INPUT_ID = 'zoe-popup-input';

interface ZoePopupProps {
  open: boolean;
  /** Label of the page the pop-up was opened over — display only. The question
   *  is sent exactly as typed: page/period context is not injected (see the
   *  time_window note in ZoeConversationContext). */
  pageLabel?: string;
  onClose: () => void;
  onOpenFullPage: () => void;
  /** Called when the pop-up closes while focus is inside it, before it goes
   *  inert — the launcher moves focus somewhere sensible. */
  onReturnFocus: () => void;
}

/**
 * The global ZOE pop-up: the same conversation, question bar, answer cards
 * and follow-ups as the full ZOE page, in a floating panel. The panel stays
 * mounted once opened (just hidden) so the draft question and scroll position
 * survive closing it; the thread itself only renders while open, so a hidden
 * pop-up does no typewriter or re-render work.
 */
export default function ZoePopup({ open, pageLabel, onClose, onOpenFullPage, onReturnFocus }: ZoePopupProps) {
  const { turns, isLoading, ask, newConversation, entitySuggestions, chipIntroOnboarded, ensureResumed, resumingId } =
    useZoeConversation();
  const [draft, setDraft] = useState('');
  const [wide, setWide] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const latestTurnRef = useRef<HTMLDivElement | null>(null);
  const savedScrollTopRef = useRef(0);
  const seenLatestKeyRef = useRef('');

  // Closed: move focus out first, then take the panel out of the tab order and
  // the accessibility tree — not just invisible.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (!open) {
      if (panel.contains(document.activeElement)) onReturnFocus();
      if (bodyRef.current) savedScrollTopRef.current = bodyRef.current.scrollTop;
    }
    panel.toggleAttribute('inert', !open);
  }, [open, onReturnFocus]);

  useEffect(() => {
    if (open) document.getElementById(ZOE_POPUP_INPUT_ID)?.focus();
  }, [open]);

  // T30 — the first time ZOE shows in this tab, reopen the conversation it
  // was in before a reload (the full page does the same when it mounts).
  useEffect(() => {
    if (open) ensureResumed();
  }, [open, ensureResumed]);

  // Scroll: jump to the newest turn when it starts or settles (including one
  // that settled while the pop-up was closed); otherwise reopen where the
  // user left off.
  const latestTurn = turns[turns.length - 1];
  const latestKey = latestTurn ? `${latestTurn.id}:${latestTurn.status}` : '';
  useLayoutEffect(() => {
    if (!open) return;
    if (latestKey && latestKey !== seenLatestKeyRef.current) {
      seenLatestKeyRef.current = latestKey;
      latestTurnRef.current?.scrollIntoView?.({ block: 'start' });
    } else if (bodyRef.current) {
      bodyRef.current.scrollTop = savedScrollTopRef.current;
    }
  }, [latestKey, open]);

  const submit = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;
    setDraft('');
    void ask(trimmed);
  };

  // Esc closes the pop-up only when focus is inside it, so it never also
  // closes a page modal that has its own Esc handling.
  const onPanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  const size = wide
    ? 'sm:w-155 h-viewport-8rem'
    : 'sm:w-105 h-zoe-popup';

  return (
    <div
      ref={panelRef}
      id={ZOE_POPUP_ID}
      role="dialog"
      aria-modal="false"
      aria-label="ZOE"
      aria-hidden={!open}
      onKeyDown={onPanelKeyDown}
      className={`fixed z-40 bottom-24 right-3 left-3 sm:left-auto sm:right-6 ${size} flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/60 transition-opacity-transform duration-150 ease-out motion-reduce:transition-none ${
        open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}
    >
      {/* Header */}
      <div className="flex flex-col gap-1.5 px-4 pt-3 pb-2.5 border-b border-card bg-gradient-to-b from-zoe-500/10 to-transparent">
        <div className="flex items-center gap-2">
          <ZoeMark className="w-5 h-5 text-zoe-300" />
          <span className="text-base font-semibold text-zoe-200">ZOE</span>
          <div className="ml-auto flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={newConversation}
              disabled={isLoading || turns.length === 0}
              title="New conversation"
              aria-label="New conversation"
              className="hover:bg-card disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={() => setWide((w) => !w)}
              title={wide ? 'Make narrower' : 'Make wider'}
              aria-label={wide ? 'Make narrower' : 'Make wider'}
              className="hidden sm:inline-flex hover:bg-card"
            >
              {wide ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onClose}
              title="Close (Esc)"
              aria-label="Close"
              className="hover:bg-card"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {pageLabel ? (
          <div className="text-xs text-muted-foreground">
            Opened from{' '}
            <span className="px-1.5 py-px rounded-md border border-border bg-card/60 text-foreground">{pageLabel}</span>
          </div>
        ) : null}
      </div>

      {/* Thread */}
      <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4 text-sm">
        {turns.length === 0 && resumingId ? (
          <p className="flex items-center justify-center gap-2 pt-6 text-muted-foreground" role="status">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            Opening your conversation…
          </p>
        ) : turns.length === 0 ? (
          <div className="flex flex-col items-center text-center gap-2 pt-6">
            <span className="w-12 h-12 rounded-full grid place-items-center bg-zoe-500/10 border border-zoe-500/30">
              <ZoeMark className="w-6 h-6 text-zoe-300" />
            </span>
            <p className="text-base font-semibold text-foreground">Ask ZOE anything about your fleet</p>
            <p className="text-muted-foreground">The same answers as the full ZOE page, with the evidence behind them.</p>
          </div>
        ) : open ? (
          <ZoeThread latestTurnRef={latestTurnRef} className="space-y-4" />
        ) : null}
      </div>

      {/* Ask: the same question bar as the full ZOE page (entity suggestions,
          suggested prompts), compact. Prompts only before the first question,
          where the page's empty state would show them — the panel is small. */}
      <div className="border-t border-card bg-background/60 px-3 pt-3 pb-3">
        <QueryBar
          compact
          accent="zoe"
          inputId={ZOE_POPUP_INPUT_ID}
          inputAriaLabel="Ask ZOE"
          onSubmit={submit}
          isLoading={isLoading}
          zoeChipIntroOnboarded={chipIntroOnboarded}
          placeholder="Ask ZOE about an order, vehicle, driver…"
          suggestions={entitySuggestions}
          value={draft}
          onValueChange={setDraft}
          primarySuggestedPrompts={ZOE_PRIMARY_PROMPTS}
          moreSuggestedPrompts={ZOE_MORE_PROMPTS}
          highlightPattern={PROFIT_HIGHLIGHTED_CHIP_WORDS_RE}
          showSuggestedPrompts={turns.length === 0}
          submitLabel="Ask"
          loadingLabel="Thinking…"
        />
        <div className="mt-2 flex items-center justify-end text-xs">
          <Button
            type="button"
            variant="link"
            onClick={onOpenFullPage}
            className="gap-1 font-semibold text-zoe-300 no-underline hover:underline hover:text-zoe-200"
          >
            Open full ZOE
            <ExternalLink className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
